import dayjs, { Dayjs } from 'dayjs'
import { Modal, notification } from 'antd'
import { useState } from 'react'
import connect from '../../../../helpers/api/connect'
import { useCreateFormOrderMutation } from '../../../../store/api/form-orders-api'
import { formSetsApi } from '../../../../store/api/form-sets-api'
import { useCreateFormMutation, useUpdateFormMutation } from '../../../../store/api/forms-api'
import { useUpdateShipmentMutation } from '../../../../store/api/shipments-api'
import { useUpdateTaskMutation } from '../../../../store/api/tasks-api'
import { useAppDispatch } from '../../../../store/store'
import Form from '../../../../types/api/form'
import FormOrder from '../../../../types/api/form-order'
import FormSet from '../../../../types/api/form-set'
import Task from '../../../../types/api/task'
import { buildFormNum } from './helpers/build-form-num'
import { NumberingMode, shouldKeepNum } from './types/order-draft'
import Shipment from '../../../../types/api/shipment'
import {
    buildFormOrderSaveDetails,
    getFormOrderDetailsFields,
} from '../../helpers/form-order-details'

export interface SavedFormOrder {
    documentId: string
    id: number
    formNum: string
    estimatedDate: Dayjs
    hasNewFormNums: boolean
}

interface SaveArgs {
    task: Task
    formSet: FormSet
    sourceForms: Form[]
    numberingMode: NumberingMode
    keepNumByFormId: Record<string, boolean>
    estimatedDate: Dayjs
    supplierDocId?: string
    stampId: string
    shipment?: Shipment
}

/**
 * Два шага, не смешивать:
 * - save — заказ + новые пластины, задача остаётся открытой (нужны номера для макета).
 * - complete — только статус «Завершена».
 */
export const useSaveFormOrder = () => {
    const dispatch = useAppDispatch()
    const [createFormOrder] = useCreateFormOrderMutation()
    const [createForm] = useCreateFormMutation()
    const [updateForm] = useUpdateFormMutation()
    const [updateShipment] = useUpdateShipmentMutation()
    const [updateTask] = useUpdateTaskMutation()
    const [isSaving, setIsSaving] = useState(false)
    const [isCompleting, setIsCompleting] = useState(false)

    const save = async (args: SaveArgs): Promise<SavedFormOrder | null> => {
        const {
            task,
            formSet,
            sourceForms,
            numberingMode,
            keepNumByFormId,
            estimatedDate,
            supplierDocId,
            stampId,
            shipment,
        } = args

        if (!sourceForms.length) {
            Modal.error({ title: 'Добавьте хотя бы одну форму в заказ' })
            return null
        }
        if (!dayjs.isDayjs(estimatedDate)) {
            Modal.error({ title: 'Укажите предполагаемую дату поставки' })
            return null
        }

        if (!supplierDocId) {
            Modal.error({ title: 'Укажите поставщика форм' })
            return null
        }

        setIsSaving(true)
        try {
            const { data: formOrder } = await createFormOrder({
                data: {
                    estimatedDate: estimatedDate.toISOString(),
                    orderStatus: 'Создан',
                    task: { set: [task.documentId] },
                    shipment: connect(shipment),
                    ...(supplierDocId ? { supplier: { set: [supplierDocId] } } : {}),
                },
            }).unwrap()

            const formNum = buildFormNum(formSet.id, formOrder.id)

            // Каждая позиция заказа — новая Form B. Источник A не переезжает на новый заказ
            // (иначе старый Form Order потеряет состав).
            for (const source of sourceForms) {
                const keep = shouldKeepNum(numberingMode, !!keepNumByFormId[source.documentId])

                await createForm({
                    data: {
                        color: source.color,
                        colorName: source.colorName,
                        inStock: false,
                        form_set: connect(formSet),
                        form_order: connect(formOrder),
                        ...(source.polymer ? { polymer: source.polymer } : {}),
                        ...(keep
                            ? {
                                  ...(source.num ? { num: source.num } : {}),
                                  ...(source.oldNum ? { oldNum: source.oldNum } : {}),
                              }
                            : {
                                  num: formNum,
                                  ...(source.num ? { oldNum: source.num } : {}),
                              }),
                    },
                }).unwrap()

                // Источник только отвязываем от набора, не удаляем
                await updateForm({
                    docId: source.documentId,
                    body: {
                        data: {
                            form_set: { disconnect: [formSet.documentId] },
                        },
                    },
                }).unwrap()
            }

            if (task.shipment?.documentId && !task.shipment.form_set) {
                await updateShipment({
                    docId: task.shipment.documentId,
                    body: {
                        data: { form_set: { set: [formSet.documentId] } },
                    },
                }).unwrap()
            }

            const details = buildFormOrderSaveDetails(task, stampId, formOrder.documentId)

            // Статус не трогаем: дизайнер ещё копирует номера и идёт в стороннюю систему
            await updateTask({
                docId: task.documentId,
                body: { data: { details } },
            }).unwrap()

            dispatch(formSetsApi.util.invalidateTags(['FormSet']))
            notification.success({ message: 'Заказ форм сохранён' })

            return {
                documentId: formOrder.documentId,
                id: formOrder.id,
                formNum,
                estimatedDate,
                hasNewFormNums: sourceForms.some(
                    source => !shouldKeepNum(numberingMode, !!keepNumByFormId[source.documentId])
                ),
            }
        } catch (error) {
            console.error(error)
            Modal.error({
                title: 'Не удалось сохранить заказ',
                content:
                    'Если заказ уже создался, проверьте вкладку «Ожидаемые» и статус задачи вручную.',
            })
            return null
        } finally {
            setIsSaving(false)
        }
    }

    const complete = async (task: Task, formOrder: Pick<FormOrder, 'documentId'>) => {
        const stampId =
            task.shipment?.stamp?.documentId || getFormOrderDetailsFields(task).stampId

        if (!stampId) {
            Modal.error({ title: 'Не найден stampId для задачи' })
            return false
        }

        setIsCompleting(true)
        try {
            const details = buildFormOrderSaveDetails(task, stampId, formOrder.documentId)

            await updateTask({
                docId: task.documentId,
                body: {
                    data: { taskStatus: 'Завершена', details },
                },
            }).unwrap()

            notification.success({ message: 'Задача закрыта' })
            return true
        } catch (error) {
            console.error(error)
            Modal.error({ title: 'Не удалось завершить задачу' })
            return false
        } finally {
            setIsCompleting(false)
        }
    }

    return { save, complete, isSaving, isCompleting }
}
